import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function ActivationEnergyCalculator() {
  const { t } = useTranslation();
  const [k1, setK1] = useState("");
  const [k2, setK2] = useState("");
  const [t1, setT1] = useState("");
  const [t2, setT2] = useState("");
  const [result, setResult] = useState<number | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: t('nav.home'), url: 'https://calclab.app' },
      { name: t('categories.chemistry'), url: 'https://calclab.app/chemistry' },
      { name: "Activation Energy Calculator", url: "https://calclab.app/calculator/activation-energy" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Activation Energy Calculator",
      "Calculate activation energy using the Arrhenius equation. Enter rate constants and temperatures to find Ea in kJ/mol instantly.",
      "https://calclab.app/calculator/activation-energy",
      lang
    )
  ]);

  const calculate = () => {
    const rate1 = parseFloat(k1);
    const rate2 = parseFloat(k2);
    const temp1 = parseFloat(t1);
    const temp2 = parseFloat(t2);

    if (isNaN(rate1) || isNaN(rate2) || isNaN(temp1) || isNaN(temp2) || 
        rate1 <= 0 || rate2 <= 0 || temp1 <= 0 || temp2 <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid positive numbers.",
        variant: "destructive",
      });
      return;
    }

    const R = 8.314; // J/(mol·K)
    
    // Arrhenius equation: ln(k2/k1) = (Ea/R)(1/T1 - 1/T2)
    const ea = R * Math.log(rate2 / rate1) / ((1/temp1) - (1/temp2));
    const eaKJ = ea / 1000; // Convert to kJ/mol
    
    setResult(eaKJ);

    toast({
      title: "Calculation Complete",
      description: `Ea = ${eaKJ.toFixed(2)} kJ/mol`,
    });
  };

  const reset = () => {
    setK1("");
    setK2("");
    setT1("");
    setT2("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Activation Energy Calculator" 
      calculatorPath="/calculator/activation-energy"
      category="chemistry"
      subcategory="Kinetics"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Activation Energy Calculator</CardTitle>
            <CardDescription>
              Calculate Ea using the Arrhenius equation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="k1">Rate Constant k₁</Label>
                  <Input
                    id="k1"
                    type="number"
                    step="any"
                    value={k1}
                    onChange={(e) => setK1(e.target.value)}
                    placeholder="k at T₁"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="t1">Temperature T₁ (K)</Label>
                  <Input
                    id="t1"
                    type="number"
                    step="any"
                    value={t1}
                    onChange={(e) => setT1(e.target.value)}
                    placeholder="Temperature 1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="k2">Rate Constant k₂</Label>
                  <Input
                    id="k2"
                    type="number"
                    step="any"
                    value={k2}
                    onChange={(e) => setK2(e.target.value)}
                    placeholder="k at T₂"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="t2">Temperature T₂ (K)</Label>
                  <Input
                    id="t2"
                    type="number"
                    step="any"
                    value={t2}
                    onChange={(e) => setT2(e.target.value)}
                    placeholder="Temperature 2"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Activation Energy (Ea)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-start justify-between gap-4">
                      <p className="text-2xl font-bold break-all overflow-wrap-anywhere flex-1">{result.toFixed(2)} kJ/mol</p>
                      <CopyResultButton value={`${result.toFixed(2)} kJ/mol`} />
                    </div>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["activation-energy"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Arrhenius Equation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-mono text-sm">ln(k₂/k₁) = (Ea/R)(1/T₁ - 1/T₂)</p>
                <p className="text-sm text-muted-foreground mt-2">
                  R = 8.314 J/(mol·K)
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
